import cn from 'clsx'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { connectHits } from 'react-instantsearch-dom'
import Slider from 'react-slick'

interface Props {
  title: string
  columns: number
  hits: []
  HitComponent: any
}

const CollectionProducts: FC<Props> = ({
  title,
  columns,
  hits,
  HitComponent,
}) => {
  const sortedHits = [...hits]
  const sliderRef = useRef<any>(null)
  const theNumberOfColumns: number = Number(columns)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [slidesVisible, setSlidesVisible] = useState<number>(() => {
    if (typeof window === 'undefined') return theNumberOfColumns
    const width = window.innerWidth
    if (width < 768) return 2.5
    if (width < 1024) return 4.5
    return theNumberOfColumns
  })
  const [isAtEnd, setIsAtEnd] = useState(false)

  useEffect(() => {
    const updateSlidesVisible = () => {
      const width = window.innerWidth

      if (width < 768) {
        setSlidesVisible(2.5)
        return
      }

      if (width < 1024) {
        setSlidesVisible(4.5)
        return
      }

      setSlidesVisible(theNumberOfColumns)
    }

    updateSlidesVisible()
    window.addEventListener('resize', updateSlidesVisible)

    return () => {
      window.removeEventListener('resize', updateSlidesVisible)
    }
  }, [theNumberOfColumns])

  const updateEndState = useCallback(
    (currentSlide: number) => {
      if (sortedHits.length === 0) return
      const maxStartIndex = Math.max(0, sortedHits.length - slidesVisible)
      setIsAtEnd(currentSlide >= maxStartIndex - 0.01)
    },
    [sortedHits.length, slidesVisible]
  )

  useEffect(() => {
    updateEndState(0)
  }, [updateEndState])

  // Reset position whenever data or breakpoint changes
  useEffect(() => {
    setCurrentSlide(0)
    setIsAtEnd(false)
  }, [slidesVisible, sortedHits.length])

  const scrollBy =
    slidesVisible <= 2.5 ? 2 : slidesVisible <= 4.5 ? 4 : theNumberOfColumns
  const dotCount =
    sortedHits.length === 0 ? 0 : Math.ceil(sortedHits.length / scrollBy)
  const activeDot =
    sortedHits.length === 0
      ? 0
      : isAtEnd
      ? dotCount - 1
      : Math.max(0, Math.min(Math.floor(currentSlide / scrollBy), dotCount - 1))

  const sliderKey = `${slidesVisible}-${sortedHits.length}`

  const sliderSettings = {
    className: cn({ 'hide-slider': sortedHits.length < theNumberOfColumns }),
    dots: false,
    infinite: false,
    initialSlide: 0,
    speed: 500,
    slidesToShow: theNumberOfColumns,
    slidesToScroll: theNumberOfColumns,
    arrows: true,
    beforeChange: (_: number, nextSlide: number) => {
      setCurrentSlide(nextSlide)
      updateEndState(nextSlide)
    },
    afterChange: (slide: number) => {
      setCurrentSlide(slide)
      updateEndState(slide)
    },
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          className: cn({ 'hide-slider': sortedHits.length < 5 }),
          slidesToShow: 4.5,
          slidesToScroll: 4,
          arrows: true,
        },
      },
      {
        breakpoint: 768,
        settings: {
          className: cn({ 'hide-slider': sortedHits.length < 3 }),
          slidesToShow: 2.5,
          slidesToScroll: 2,
          arrows: false,
        },
      },
    ],
  }

  return (
    <div
      className={cn('wrapper my-10 laptop:my-20 product-slick grid-slick', {
        'grid-slick-at-end': isAtEnd,
        hidden: sortedHits.length === 0,
      })}
    >
      <div className="uppercase text-text-dark text-mobileh4 md:text-h4">
        {title}
      </div>
      <div className="mt-8">
        <Slider ref={sliderRef} key={sliderKey} {...sliderSettings}>
          {sortedHits.map((hit: any, index: number) => {
            return <HitComponent key={index} hit={hit} />
          })}
        </Slider>
        {dotCount > 1 && (
          <ul className="slick-dots">
            {Array.from({ length: dotCount }, (_, i) => (
              <li key={i} className={i === activeDot ? 'slick-active' : ''}>
                <button
                  type="button"
                  onClick={() => sliderRef.current?.slickGoTo(i * scrollBy)}
                >
                  {i + 1}
                </button>
              </li>
            ))}
          </ul>
        )}
        <div
          className={cn('gap-4 tablet:gap-6 laptop:gap-8', {
            hidden: sortedHits.length > 4,
            'laptop:flex mobileXS:hidden tablet:hidden':
              sortedHits.length === 3 || sortedHits.length === 4,
            'tablet:flex mobileXS:hidden': sortedHits.length === 2,
            'mobileXS:flex': sortedHits.length === 1,
          })}
        >
          {sortedHits.map((hit: any, index: number) => {
            return (
              <div
                key={hit.id}
                className="w-32 mobileM:w-40 mobileL:w-48 tablet:w-52 desktop:w-56"
              >
                <HitComponent key={index} hit={hit} />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default connectHits(CollectionProducts)
